import React from 'react';
import {
  Smartphone,
  Monitor,
  Bell,
  RefreshCw,
  UserCheck,
  Shield,
  CheckCircle2,
  ExternalLink,
  LogOut,
  Zap,
  Database,
} from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import { ScaleSupportLogo } from '../common/ScaleSupportLogo';

import { todayLocal } from '../../utils/dates';
export const TopHeader: React.FC<{
  onOpenSyncModal?: () => void;
  onOpenNotifications?: () => void;
}> = ({ onOpenSyncModal, onOpenNotifications }) => {
  const {
    currentUser,
    logout,
    deviceMode,
    setDeviceMode,
    notifications,
    commissionSettings,
    autoSyncEnabled,
    isSyncing,
    lastAutoSyncTime,
    firestoreSpeedStats,
    ptps,
    ackedReminders,
    visibleNotice,
  } = useSRMS();

  /**
   * The bell counts what is actually WAITING for this person.
   *
   * That is unread notifications plus any promise to pay that has come due
   * and not yet been acknowledged -- which is the reminder that matters most
   * and used to appear nowhere but a banner you could scroll past. The count
   * only drops when somebody presses OK, which is the point.
   */
  const todayIsoForBell = todayLocal();
  const duePtpCount = (ptps || []).filter((p) => {
    if (p.status !== 'Pending') return false;
    if ((p.ptpDate || '') > todayIsoForBell) return false;
    if (ackedReminders.includes(`PTPREM-${p.id}`)) return false;
    if (currentUser.role === 'admin' || currentUser.role === 'management') return true;
    const who = (p.agentId || '').trim().toLowerCase();
    return (
      who === (currentUser.agentId || '').trim().toLowerCase() ||
      who === (currentUser.id || '').trim().toLowerCase()
    );
  }).length;

  const unreadCount = notifications.filter((n) => !n.read).length + duePtpCount;

  const roleColors: Record<string, string> = {
    admin: 'bg-purple-100 text-purple-700 border-purple-200',
    coordinator: 'bg-blue-100 text-blue-700 border-blue-200',
    agent: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    management: 'bg-amber-100 text-amber-700 border-amber-200',
  };

  return (
    <header className="bg-[#0f172a] text-slate-200 border-b border-slate-800 sticky top-0 z-40 px-2.5 sm:px-5 py-2 sm:py-2.5 flex items-center justify-between shadow-sm w-full max-w-full overflow-hidden">
      {/* No logo here. The sidebar carries it, and the same mark twice on
          one screen is clutter -- which is what the small one in this bar
          was. This side is left empty so the controls sit right. */}

      {/* THE ADMINISTRATOR'S NOTICE.
          This space was simply empty. It now carries whatever the
          administrator has posted, scrolling across, in the colour they
          chose. With nothing posted the space stays blank rather than
          scrolling an empty ribbon. */}
      <div className="flex-1 min-w-0 overflow-hidden px-2 sm:px-4">
        {visibleNotice?.text ? (
          <div
            className={`relative overflow-hidden rounded-lg border px-2 py-1 ${
              visibleNotice.colour === 'red'
                ? 'bg-rose-950/60 border-rose-800 text-rose-200'
                : visibleNotice.colour === 'amber'
                ? 'bg-amber-950/60 border-amber-800 text-amber-200'
                : 'bg-emerald-950/60 border-emerald-800 text-emerald-200'
            }`}
            title={visibleNotice.text}
          >
            <div className="srms-ticker whitespace-nowrap text-[11px] sm:text-xs font-semibold">
              {visibleNotice.text}
            </div>
          </div>
        ) : null}
      </div>

      {/* Right User & Controls -- the Android / Web switch now lives here,
          beside the user, instead of floating in the middle of the bar. */}
      <div className="flex items-center space-x-1.5 sm:space-x-3 shrink-0">
      <div className="flex items-center bg-slate-900/90 p-0.5 sm:p-1 rounded-lg sm:rounded-xl border border-slate-800 shrink-0 mx-1 sm:mx-2">
        <button
          id="btn-switch-android"
          onClick={() => setDeviceMode('android')}
          className={`flex items-center space-x-1 sm:space-x-1.5 px-2 sm:px-3.5 py-1 sm:py-1.5 rounded-md sm:rounded-lg text-[11px] sm:text-xs font-semibold transition-all cursor-pointer ${
            deviceMode === 'android'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/80'
          }`}
          title="Switch to Android Mobile App view"
        >
          <Smartphone className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">📱 Android App</span>
          <span className="sm:hidden">App</span>
        </button>

        <button
          id="btn-switch-web"
          onClick={() => setDeviceMode('web')}
          className={`flex items-center space-x-1 sm:space-x-1.5 px-2 sm:px-3.5 py-1 sm:py-1.5 rounded-md sm:rounded-lg text-[11px] sm:text-xs font-semibold transition-all cursor-pointer ${
            deviceMode === 'web'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/80'
          }`}
          title="Switch to Web Portal view"
        >
          <Monitor className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">💻 Web Portal</span>
          <span className="sm:hidden">Web</span>
        </button>
      </div>

        {/* Cloud Firestore Service Optimization Status - ADMIN ONLY */}
        {currentUser?.role === 'admin' && (
          <button
            id="btn-sync-storage"
            onClick={onOpenSyncModal}
            className="hidden xl:flex items-center space-x-1.5 px-2.5 py-1.5 bg-slate-800/80 hover:bg-slate-800 border border-slate-700/80 rounded-xl text-xs text-slate-200 transition shadow-sm cursor-pointer"
            title={`Cloud Firestore Service Optimization: Sub-${firestoreSpeedStats?.averageLatencyMs || 25}ms Latency • Real-time Auth Snapshots • Long-Polling Resilient`}
          >
            {isSyncing ? (
              <RefreshCw className="w-3.5 h-3.5 text-blue-400 animate-spin" />
            ) : (
              <div className="flex items-center gap-1">
                <Zap className="w-3.5 h-3.5 text-amber-400 fill-amber-400/20" />
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              </div>
            )}
            <span className="font-medium">Firestore Optimized</span>
            <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-1 py-0.2 rounded border border-emerald-500/20">
              ~{firestoreSpeedStats?.averageLatencyMs || 25}ms
            </span>
          </button>
        )}

        {/*
          THE READ COUNTER.

          Firestore bills per document read, and until this existed there was
          no way to see that number without opening the Firebase console the
          next day. Every judgement about cost was a guess.

          WHAT THE TWO FIGURES MEAN:

            BILLED (amber)  -- documents the server sent. These cost money.
            CACHED (green)  -- documents answered from the browser's own copy.
                               These are free, and every one of them would
                               have been billed before the local cache was
                               switched on.

          So the green figure is the saving, live.

          WHAT IT IS NOT. It counts THIS BROWSER WINDOW only. Other people
          signing in on their own devices move the project's real usage and
          never touch this number, and it restarts at zero on every page
          reload. It is for spot-checking one session -- open the app, work,
          sign out and back in, and green should climb while amber barely
          moves. For a figure covering everybody, Firebase Console -> Usage
          and billing is the only honest source.

          Administrators only -- an agent has no use for it.
        */}
        {(currentUser?.role === 'admin' || (currentUser?.role as string) === 'super_admin') && (
          <div
            className="hidden lg:flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-800/80 border border-slate-700/80 rounded-xl text-xs shrink-0"
            title={
              'Documents read by THIS browser window, since the page loaded.\n\n' +
              `Billed (from the server): ${(firestoreSpeedStats?.billedDocumentReads ?? 0).toLocaleString('en-IN')}\n` +
              `Free (from the local cache): ${(firestoreSpeedStats?.cachedDocumentReads ?? 0).toLocaleString('en-IN')}\n\n` +
              'Only the billed figure reaches your Firebase bill. This covers your own window only \u2014 other users are not counted here. For everybody, see Firebase Console \u2192 Usage and billing.'
            }
          >
            <Database className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-slate-400 font-medium">Reads</span>
            <span className="font-mono text-amber-300 bg-amber-500/10 px-1 rounded border border-amber-500/20">
              {(firestoreSpeedStats?.billedDocumentReads ?? 0).toLocaleString('en-IN')}
            </span>
            <span className="text-slate-600">/</span>
            <span
              className="font-mono text-emerald-400 bg-emerald-500/10 px-1 rounded border border-emerald-500/20"
              title="Served free from the local cache"
            >
              {(firestoreSpeedStats?.cachedDocumentReads ?? 0).toLocaleString('en-IN')}
            </span>
          </div>
        )}

        {/* Notification Bell */}
        <button
          id="btn-notifications"
          onClick={onOpenNotifications}
          className="relative p-1.5 sm:p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg sm:rounded-xl transition shrink-0"
          title="System & WhatsApp Notifications"
        >
          <Bell className="w-4 h-4" />
          {unreadCount > 0 && (
            <span className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1 w-3.5 h-3.5 sm:w-4 sm:h-4 bg-red-500 text-white text-[9px] sm:text-[10px] font-bold rounded-full flex items-center justify-center animate-bounce">
              {unreadCount}
            </span>
          )}
        </button>

        {/* Authenticated User Profile Badge & Logout */}
        <div className="flex items-center space-x-1.5 sm:space-x-2.5 pl-1.5 sm:pl-3 border-l border-slate-800 shrink-0">
          <div className="flex items-center gap-2 bg-slate-900/90 border border-slate-800 rounded-lg sm:rounded-xl px-2 sm:px-2.5 py-1 sm:py-1.5">
            <div className="w-6 h-6 rounded-md bg-blue-600/30 border border-blue-500/40 text-blue-300 font-bold text-[11px] flex items-center justify-center">
              {currentUser.name.slice(0, 1).toUpperCase()}
            </div>
            <div className="text-left">
              <div className="text-xs font-bold text-white flex items-center gap-1.5 leading-tight">
                <span className="truncate max-w-[90px] sm:max-w-[120px] md:max-w-[160px]">{currentUser.name}</span>
                {currentUser.agentId && (
                  <span className="text-[10px] font-mono bg-blue-600/30 text-blue-300 border border-blue-500/30 px-1.5 py-0.2 rounded font-semibold hidden xs:inline">
                    {currentUser.agentId}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 mt-0.5">
                <span
                  className={`text-[9px] font-semibold uppercase px-1.5 py-0.2 rounded inline-block border ${
                    roleColors[currentUser.role] || 'bg-slate-800 text-slate-300 border-slate-700'
                  }`}
                >
                  {currentUser.role}
                </span>
                <span className="text-[10px] text-slate-400 hidden sm:inline truncate max-w-[100px]">
                  • {currentUser.branch || 'Branch'}
                </span>
              </div>
            </div>
          </div>

          {/* Logout Button */}
          <button
            id="btn-logout"
            onClick={logout}
            className="flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-red-600/15 hover:bg-red-600/30 border border-red-500/40 text-red-300 hover:text-red-100 rounded-lg sm:rounded-xl text-xs font-semibold transition cursor-pointer shrink-0"
            title="Sign out of current account session"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
};
